/**
 * 控制台首页 - 群组列表
 * 参考 openlink.chat 设计风格
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { getMe } from '../../api/auth';
import { getGroups, deleteGroup, Group } from '../../api/groups';
import { toast } from 'sonner';
import { bindRobot, getRobotBindings, RobotBinding } from '../../api/robotBindings';

interface UserInfo {
  id: string;
  email: string;
  nickname?: string;
  group_count: number;
  total_robots: number;
}

// 内联 SVG 图标
const Icons = {
  Plus: ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  Users: ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Server: ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
    </svg>
  ),
  Trash2: ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  Edit2: ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  Bolt: ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
};

export default function Dashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [bindings, setBindings] = useState<RobotBinding[]>([]);
  const [robotAk, setRobotAk] = useState('');
  const [binding, setBinding] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [info, groupsData, boundRobots] = await Promise.all([getMe(), getGroups(1, 100), getRobotBindings()]);
      setUserInfo(info);
      setGroups(groupsData.items);
      setBindings(boundRobots);
    } catch (error) {
      toast.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  }

  async function handleBind(event: React.FormEvent) {
    event.preventDefault();
    if (!robotAk.trim()) return;
    setBinding(true);
    try {
      await bindRobot(robotAk.trim());
      setRobotAk('');
      toast.success('机器人绑定成功');
      await loadData();
    } catch { /* API interceptor presents the error. */ }
    finally { setBinding(false); }
  }

  async function handleDelete(groupId: string, groupName: string) {
    if (!confirm(`确定要删除群组"${groupName}"吗？`)) return;
    try {
      await deleteGroup(groupId);
      toast.success('群组已删除');
      loadData();
    } catch (error: any) {
      toast.error(error.message || '删除失败');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lobster-red"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">欢迎回来，{userInfo?.nickname || user?.nickname || '朋友'}</h1>
        <p className="text-gray-500 mt-1">管理你的机器人和群组</p>
      </div>

      {/* 关键指标卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">我的群组</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {userInfo?.group_count || 0}
                <span className="text-base font-normal text-gray-500">/3</span>
              </p>
            </div>
            <div className="p-3 bg-lobster-red/10 rounded-lg">
              <Icons.Users className="w-8 h-8 text-lobster-red" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">机器人总数</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{userInfo?.total_robots || 0}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Icons.Server className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">可创建群组</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{Math.max(0, 3 - (userInfo?.group_count || 0))}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Icons.Bolt className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 绑定机器人之后，作为群成员参与配额与列表。 */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 p-6">
        <h2 className="text-lg font-bold text-gray-900">绑定我的机器人</h2>
        <p className="text-sm text-gray-500 mt-1">输入机器人的 AK 验证归属；绑定后该机器人已加入的群会出现在“我的群组”中并计入群组上限。请勿将 AK 提供给他人。</p>
        <form onSubmit={handleBind} className="flex flex-wrap gap-2 mt-4">
          <input type="password" autoComplete="off" value={robotAk} onChange={event => setRobotAk(event.target.value)}
            placeholder="机器人 AK" aria-label="机器人 AK" className="flex-1 min-w-48 border border-gray-300 rounded-lg px-3 py-2" />
          <button disabled={binding || !robotAk.trim()} type="submit" className="px-4 py-2 bg-lobster-red text-white rounded-lg disabled:opacity-50">绑定</button>
        </form>
        {bindings.length > 0 && <p className="text-sm text-gray-600 mt-3">已绑定：{bindings.map(item => item.robot_name).join('、')}</p>}
      </section>

      {/* 群组列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">我的群组</h2>
            <p className="text-sm text-gray-500 mt-1">共 {userInfo?.group_count || 0} 个群组</p>
          </div>
          <button
            onClick={() => navigate('/groups/new')}
            disabled={(userInfo?.group_count || 0) >= 3}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-lobster-red rounded-lg hover:bg-lobster-red/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icons.Plus className="w-4 h-4 mr-2" />
            创建群组
          </button>
        </div>

        {groups.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <Icons.Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">还没有群组</h3>
            <p className="text-gray-600 mb-6">创建你的第一个群组，开始管理机器人</p>
            <button
              onClick={() => navigate('/groups/new')}
              disabled={(userInfo?.group_count || 0) >= 3}
              className="px-6 py-2 bg-lobster-red text-white rounded-lg hover:bg-lobster-red/90 font-medium disabled:opacity-50"
            >
              创建第一个群组
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {groups.map((group) => (
              <div
                key={group.id}
                className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow bg-white"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 truncate max-w-[180px]" title={group.group_name}>
                    {group.group_name}
                  </h3>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs ml-2">
                    {group.external_platform || '未绑定'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <Icons.Server className="w-4 h-4" />
                  <span>{group.robot_count}/9 机器人 · {group.is_owner ? '群主' : '已加入'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/groups/${group.id}`)}
                    className="flex-1 bg-lobster-red text-white py-2 rounded-lg text-sm font-medium hover:bg-lobster-red/90"
                  >
                    管理
                  </button>
                  {group.is_owner && <button
                    onClick={() => navigate(`/groups/${group.id}/edit`)}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="编辑"
                  >
                    <Icons.Edit2 className="w-4 h-4" />
                  </button>}
                  {group.is_owner && <button
                    onClick={() => handleDelete(group.id, group.group_name)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="删除"
                  >
                    <Icons.Trash2 className="w-4 h-4" />
                  </button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
